/**
 * MY Corporate L_CONTACT_BRANCH — Show Address 팝업·draft 반영
 */
import type { ContactAddressTypeCode } from '../types/contact.types';
import type { MyLocationOption } from './contactMyLocationFields';
import {
    formatMyAddressRowBodyForDisplay,
    getMyAddressAlias,
    normalizeMyLocationRowForForm,
} from './contactMyLocationFields';

export type BranchAddressShowItem = {
    id: string;
    row: Record<string, unknown>;
    displayLine: string;
    alias: string;
    addressTypeCode?: ContactAddressTypeCode;
    addressTypeLabel?: string;
};

export type CorporateBranchShowLabels = {
    residential: string;
    service: string;
    business: string;
};

export function branchAddressRowId(row: Record<string, unknown>, index: number): string {
    const bid = row?.branch_id ?? row?.BRANCH_ID;
    if (bid != null && String(bid).trim() !== '') return String(bid);
    return `idx-${index}`;
}

/** 팝업 목록용 — 주소 본문만 (Alias는 별도 칼럼/칩) */
export function formatBranchRowDisplayLineSimple(row: Record<string, unknown> | null | undefined): string {
    const body = formatMyAddressRowBodyForDisplay(row || {});
    return body || '-';
}

export function buildBranchAddressShowItems(
    rows: any[],
    formatDisplay?: (row: any) => string,
): BranchAddressShowItem[] {
    const fmt = formatDisplay ?? formatBranchRowDisplayLineSimple;
    return (Array.isArray(rows) ? rows : []).map((row, index) => {
        const r = row || {};
        let displayLine = '-';
        try {
            displayLine = fmt(r) || '-';
        } catch {
            displayLine = formatBranchRowDisplayLineSimple(r as Record<string, unknown>);
        }
        return {
            id: branchAddressRowId(r, index),
            row: { ...(r as Record<string, unknown>) },
            displayLine,
            alias: getMyAddressAlias(r as Record<string, unknown>),
        };
    });
}

/** Registered + Service + Business — Show Address 팝업 통합 목록 */
export function buildAllCorporateBranchShowItems(input: {
    residential?: any[];
    service?: any[];
    business?: any[];
    labels: CorporateBranchShowLabels;
    formatResidential?: (row: any) => string;
    formatService?: (row: any) => string;
    formatBusiness?: (row: any) => string;
}): BranchAddressShowItem[] {
    const fmtDefault = formatBranchRowDisplayLineSimple;
    const sections: Array<{
        prefix: string;
        code: ContactAddressTypeCode;
        label: string;
        rows: any[];
        format?: (row: any) => string;
    }> = [
        {
            prefix: 'res',
            code: 'M04001',
            label: input.labels.residential,
            rows: input.residential ?? [],
            format: input.formatResidential,
        },
        {
            prefix: 'svc',
            code: 'M04002',
            label: input.labels.service,
            rows: input.service ?? [],
            format: input.formatService,
        },
        {
            prefix: 'biz',
            code: 'M04003',
            label: input.labels.business,
            rows: input.business ?? [],
            format: input.formatBusiness,
        },
    ];
    const out: BranchAddressShowItem[] = [];
    for (const section of sections) {
        const items = buildBranchAddressShowItems(section.rows, section.format ?? fmtDefault);
        for (const item of items) {
            out.push({
                ...item,
                id: `${section.prefix}-${item.id}`,
                addressTypeCode: section.code,
                addressTypeLabel: section.label,
            });
        }
    }
    return out;
}

export type ApplyBranchAddressToDraftOptions = {
    /**
     * Show Address: 편집/Add 대상 행의 BRANCH_ID·ENTITY 매핑 유지 (주소·alias만 복사).
     * false면 source의 branch_id 등을 그대로 복사 (레거시).
     */
    preserveDraftBranchIdentity?: boolean;
    /**
     * Entity_my: 선택 branch로 MAP 재지정 — draft.branch_id = source.branch_id, 주소는 표시용 복사.
     * Save 시 L_CONTACT_BRANCH_MAP 만 갱신 (entityCd 필요).
     */
    repointEntityMapBranch?: boolean;
    entityCd?: string;
};

function readDraftBranchId(draft: Record<string, unknown>): number | null {
    const bid = Number(draft?.branch_id ?? draft?.BRANCH_ID);
    return Number.isFinite(bid) && bid > 0 ? bid : null;
}

function readSourceBranchId(source: Record<string, unknown>): number | null {
    const bid = Number(source?.branch_id ?? source?.BRANCH_ID);
    return Number.isFinite(bid) && bid > 0 ? bid : null;
}

export function applyBranchAddressRowToDraft(
    draft: Record<string, unknown>,
    source: Record<string, unknown>,
    ctx: {
        countryOpts?: MyLocationOption[];
        stateOpts?: MyLocationOption[];
        cityOpts?: MyLocationOption[];
    } = {},
    options: ApplyBranchAddressToDraftOptions = {},
): void {
    if (options.repointEntityMapBranch) {
        const srcBid = readSourceBranchId(source);
        const entityCd = String(options.entityCd || draft.entity_cd || draft.ENTITY_CD || '').trim();
        draft.street01 = String(source.street01 ?? source.addressLine1 ?? '').trim();
        draft.street02 = String(source.street02 ?? source.addressLine2 ?? '').trim();
        draft.street03 = String(source.street03 ?? source.addressLine3 ?? '').trim();
        draft.post_code = String(source.post_code ?? source.postcode ?? source.postalCode ?? '').trim();
        draft.city = String(source.city ?? source.cityCode ?? '').trim();
        draft.state = String(source.state ?? source.stateCode ?? '').trim();
        draft.country = String(source.country ?? source.countryCode ?? '').trim();
        draft.alias = String(source.alias ?? source.ALIAS ?? '').trim();
        if (srcBid != null) {
            draft.branch_id = srcBid;
            draft.BRANCH_ID = srcBid;
        }
        if (entityCd) {
            draft.entity_cd = entityCd;
            draft.has_entity_map = true;
        } else if (source.entity_cd != null || source.ENTITY_CD != null) {
            draft.entity_cd = String(source.entity_cd ?? source.ENTITY_CD ?? '').trim();
            draft.has_entity_map = true;
        }
        draft._entityMapRepoint = true;
        const normalized = normalizeMyLocationRowForForm(draft, ctx);
        Object.assign(draft, normalized);
        return;
    }

    const preserve = options.preserveDraftBranchIdentity !== false;
    const identityBefore = preserve
        ? {
              branchId: readDraftBranchId(draft),
              orderNo: draft.order_no ?? draft.ORDER_NO,
              isRepresentative: draft.is_representative ?? draft.IS_REPRESENTATIVE,
              hasEntityMap: draft.has_entity_map,
              entityCd: draft.entity_cd,
              entityName: draft.entity_name,
          }
        : null;

    const s = source || {};
    draft.street01 = String(s.street01 ?? s.addressLine1 ?? '').trim();
    draft.street02 = String(s.street02 ?? s.addressLine2 ?? '').trim();
    draft.street03 = String(s.street03 ?? s.addressLine3 ?? '').trim();
    draft.post_code = String(s.post_code ?? s.postcode ?? s.postalCode ?? '').trim();
    draft.city = String(s.city ?? s.cityCode ?? '').trim();
    draft.state = String(s.state ?? s.stateCode ?? '').trim();
    draft.country = String(s.country ?? s.countryCode ?? '').trim();
    draft.alias = String(s.alias ?? s.ALIAS ?? '').trim();

    if (!preserve) {
        if (s.branch_id != null) draft.branch_id = s.branch_id;
        if (s.BRANCH_ID != null) draft.branch_id = s.BRANCH_ID;
        if (s.order_no != null) draft.order_no = s.order_no;
        if (s.is_representative != null) draft.is_representative = s.is_representative;
        if (s.has_entity_map != null) draft.has_entity_map = s.has_entity_map;
        if (s.entity_cd != null) draft.entity_cd = s.entity_cd;
        if (s.entity_name != null) draft.entity_name = s.entity_name;
    }

    const normalized = normalizeMyLocationRowForForm(draft, ctx);
    Object.assign(draft, normalized);

    if (preserve && identityBefore) {
        if (identityBefore.branchId != null) {
            draft.branch_id = identityBefore.branchId;
        } else {
            delete draft.branch_id;
            delete draft.BRANCH_ID;
        }
        if (identityBefore.orderNo != null) draft.order_no = identityBefore.orderNo;
        else delete draft.order_no;
        if (identityBefore.isRepresentative != null) {
            draft.is_representative = identityBefore.isRepresentative;
        }
        if (identityBefore.hasEntityMap != null) {
            draft.has_entity_map = identityBefore.hasEntityMap;
        } else {
            delete draft.has_entity_map;
        }
        if (identityBefore.entityCd != null) {
            draft.entity_cd = identityBefore.entityCd;
        } else {
            delete draft.entity_cd;
        }
        if (identityBefore.entityName != null) {
            draft.entity_name = identityBefore.entityName;
        } else {
            delete draft.entity_name;
        }
    }
}
